import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

const AuthContext = createContext(null);

function mapSessionUser(sessionUser) {
  if (!sessionUser) return null;
  const meta = sessionUser.user_metadata ?? {};
  return {
    id: sessionUser.id,
    email: sessionUser.email ?? '',
    full_name: meta.full_name ?? '',
    phone: meta.phone ?? '',
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [passwordRecoveryInProgress, setPasswordRecoveryInProgress] = useState(false);

  const detectRecoveryFromHash = () => {
    if (typeof window === 'undefined') return false;
    const raw = window.location.hash?.replace(/^#/, '') || '';
    const params = new URLSearchParams(raw);
    return params.get('type') === 'recovery';
  };

  useEffect(() => {
    if (!supabase) {
      setReady(true);
      return;
    }

    const applySession = (session) => {
      if (session?.user) {
        setUser(mapSessionUser(session.user));
      } else {
        setUser(null);
      }
    };

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (detectRecoveryFromHash()) {
        setPasswordRecoveryInProgress(true);
      }
      applySession(session);
      setReady(true);
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecoveryInProgress(true);
      }
      if (event === 'SIGNED_OUT') {
        setPasswordRecoveryInProgress(false);
      }
      applySession(session);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const getToken = async () => {
    if (!supabase) return null;
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  };

  const login = async (email, password) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signup = async (email, password, options = {}) => {
    if (!supabase) throw new Error('Supabase not configured');
    const redirectTo = typeof window !== 'undefined' ? window.location.origin : undefined;
    const { full_name, phone } = options;
    const { data, error } = await supabase.auth.signUp(
      { email, password, options: { data: { full_name: full_name || undefined, phone: phone || undefined } } },
      redirectTo ? { emailRedirectTo: redirectTo } : undefined,
    );
    if (error) throw error;
    return data;
  };

  const requestPasswordReset = async (email) => {
    if (!supabase) throw new Error('Supabase not configured');
    const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/` : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo,
    });
    if (error) throw error;
  };

  const completePasswordReset = useCallback(async (newPassword) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    setPasswordRecoveryInProgress(false);
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const { pathname, search } = window.location;
      window.history.replaceState(null, '', pathname + search);
    }
  }, []);

  const logout = async () => {
    setPasswordRecoveryInProgress(false);
    setUser(null);
    if (supabase) await supabase.auth.signOut();
  };

  const setDevUser = () => setUser({ id: 'dev-local', email: 'dev@localhost' });

  const value = {
    user,
    ready,
    login,
    signup,
    logout,
    getToken,
    setDevUser,
    supabaseConfigured: !!supabase,
    passwordRecoveryInProgress,
    requestPasswordReset,
    completePasswordReset,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
